import { useEffect, useRef, useState } from 'react';
import { labsUpdates, type LabsUpdatesPreference } from './labsUpdates';
import type { GetToken } from './previewNotifications';

/** Reads never subscribe. Remount per account; focus refresh reflects email opt-outs. */
export default function LabsUpdatesNotify({ getToken, onClick, onSaved }: {
  getToken: GetToken;
  onClick: () => void;
  onSaved: (notify: boolean) => void;
}) {
  const [preference, setPreference] = useState<LabsUpdatesPreference | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<'read' | 'save' | null>(null);
  const [retry, setRetry] = useState(0);
  const request = useRef<AbortController | null>(null);

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'hidden' || request.current) return;
      const controller = new AbortController();
      request.current = controller;
      setBusy(true);
      setFailed(null);
      void labsUpdates(getToken, controller.signal)
        .then(value => { if (!controller.signal.aborted) setPreference(value); })
        .catch(() => {
          if (!controller.signal.aborted) {
            console.error('[Labs updates] Could not read subscription preference.');
            setPreference(null);
            setFailed('read');
          }
        })
        .finally(() => {
          if (request.current === controller) request.current = null;
          if (!controller.signal.aborted) setBusy(false);
        });
    };
    refresh();
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
      request.current?.abort();
      request.current = null;
    };
  }, [getToken, retry]);

  const save = async (subscribed: boolean) => {
    if (request.current || !preference || preference.status === 'UNSUBSCRIBED_ALL') return;
    onClick();
    const controller = new AbortController();
    request.current = controller;
    setBusy(true);
    setFailed(null);
    try {
      const saved = await labsUpdates(getToken, controller.signal, subscribed);
      if (controller.signal.aborted) return;
      const target = subscribed ? 'SUBSCRIBED' : 'UNSUBSCRIBED';
      if (saved.status !== target && saved.status !== 'UNSUBSCRIBED_ALL') throw new Error('Preference was not confirmed.');
      setPreference(saved);
      if (saved.status === target) onSaved(subscribed);
    } catch {
      // A timed-out write may have reached HubSpot. Re-read before another action.
      if (!controller.signal.aborted) {
        console.error('[Labs updates] Could not confirm subscription change.');
        setPreference(null);
        setFailed('save');
      }
    } finally {
      if (request.current === controller) request.current = null;
      if (!controller.signal.aborted) setBusy(false);
    }
  };

  // Background failures leave the page quiet; focus/visibility can recover it.
  // An explicit failed action still needs feedback and a read-before-retry path.
  if (!preference && failed !== 'save') return null;

  const subscribed = preference?.status === 'SUBSCRIBED';
  const blocked = preference?.status === 'UNSUBSCRIBED_ALL';
  return <div className={subscribed ? 'preview-notification-saved' : undefined} aria-busy={busy}>
    <div role="status">
      {subscribed ? <div className="preview-notification-confirmation">
        {/* Lucide BellRing, ISC license: /licenses/lucide.txt */}
        <svg className="preview-notification-bell" aria-hidden="true" width="28" height="28"
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.268 21a2 2 0 0 0 3.464 0" />
          <path d="M22 8c0-2.3-.8-4.3-2-6" />
          <path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />
          <path d="M4 2C2.8 3.7 2 5.7 2 8" />
        </svg>
        <div className="preview-notification-copy">
          <p className="preview-notification-title">You’re on the list.</p>
          <p className="preview-notification-description">When something new lands in Labs, one email. Never more than one a month.</p>
        </div>
        <button className="preview-stop-notifications" type="button" disabled={busy} onClick={() => void save(false)}>
          {busy ? 'Checking…' : 'Stop updates'}
        </button>
      </div> : blocked ? <p>You’ve opted out of all 240 emails. Update your email preferences to receive Labs updates.</p>
        : preference ? <p className="preview-sr-only">Labs updates are off.</p> : null}
    </div>
    {blocked ? <>
      {preference.managePreferencesUrl
        ? <a href={preference.managePreferencesUrl} target="_blank" rel="noopener noreferrer">Manage email preferences</a>
        : <p>Use “Manage preferences” in a previous 240 email.</p>}
      <button type="button" className="preview-stop-notifications" disabled={busy} onClick={() => setRetry(value => value + 1)}>Check again</button>
    </> : !subscribed && <>
      <button className="preview-notify" type="button" disabled={busy || (!preference && !failed)}
        onClick={() => preference ? void save(true) : setRetry(value => value + 1)}>
        {busy ? 'Checking…' : !preference ? failed ? 'Try again' : 'Loading…' : 'Get Labs updates'}
      </button>
    </>}
    {failed && <p role="alert" className="preview-unavailable">Could not confirm your Labs email preference. Try again to check its current status.</p>}
  </div>;
}
